import { IPublicClientApplication } from '@azure/msal-browser';
import { getDataverseHeaders } from '../auth/authService';
import { getDataverseApiBase } from './environmentService';

// ── First-sign-in security-role provisioning ───────────────────────────
//
// Target: assign the tenant's "General User" Dagrun PM security role to a user
// on first sign-in, so every signed-in user has the broad Create/Read/Write set
// the app assumes (per-project role differentiation lives entirely in
// dagrun_pm_projectmember.dagrun_pm_role, NOT in Dataverse roles).
//
// MECHANISM (stated per task): TWO complementary pieces —
//   1. THIS client-side first-sign-in step (wired in App.tsx). It activates the
//      moment the role exists, with NO code change. It is best-effort and
//      NON-authoritative: in this delegated-auth, no-middleware app a signed-in
//      user without the "assign roles" privilege cannot grant a role to
//      themselves, so this reliably self-provisions only privileged sessions and
//      otherwise reports status without error.
//   2. A Power Automate flow triggered on systemuser create, running under an
//      elevated connection — the ROBUST per-user mechanism. It is portal-built
//      (cannot be scripted from managed solution) and is documented in
//      docs/provisioning-flow.md for Scott to deploy.
//
// This module NEVER creates, modifies, deletes, or reassigns security roles or
// any existing systemuserroles association. It only *adds* the target role to
// the current user when that role already exists and the caller is permitted —
// and no-ops (status 'role-not-found') until Scott creates the role in the
// Maker Portal.

// The role Scott will create may be named "General User" or, following the
// existing family ("Dagrun PM — Project Manager" …), "Dagrun PM — General User".
// Match by substring so provisioning activates for either spelling.
const TARGET_ROLE_MATCH = 'general user';

export type ProvisioningStatus =
  | 'assigned'          // role existed, caller had privilege, role added to user
  | 'already-had-role'  // user already has the role — nothing to do
  | 'role-not-found'    // target role does not exist yet — INERT
  | 'no-systemuser'     // could not resolve current systemuser
  | 'insufficient-privilege' // role exists but caller can't assign (expected for non-admins)
  | 'error';

export interface ProvisioningResult { status: ProvisioningStatus; detail: string; }

async function dvFetch(pca: IPublicClientApplication, path: string, init?: RequestInit): Promise<Response> {
  const headers = await getDataverseHeaders(pca);
  return fetch(`${getDataverseApiBase()}/${path}`, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
}

/** Find the target security role by name substring; null when it doesn't exist. */
async function findTargetRole(pca: IPublicClientApplication): Promise<{ roleid: string; name: string } | null> {
  const res = await dvFetch(pca, `roles?$select=name,roleid&$top=500`);
  if (!res.ok) throw new Error(`roles query failed (${res.status})`);
  const rows = (await res.json() as { value: { roleid: string; name: string }[] }).value ?? [];
  return rows.find(r => (r.name ?? '').toLowerCase().includes(TARGET_ROLE_MATCH)) ?? null;
}

/** Current user's systemuserid via WhoAmI. */
async function currentSystemUserId(pca: IPublicClientApplication): Promise<string | null> {
  const res = await dvFetch(pca, 'WhoAmI');
  if (!res.ok) return null;
  return (await res.json() as { UserId?: string }).UserId ?? null;
}

async function userHasRole(pca: IPublicClientApplication, systemUserId: string, roleId: string): Promise<boolean> {
  const res = await dvFetch(pca, `systemusers(${systemUserId})/systemuserroles_association?$select=roleid&$filter=roleid eq ${roleId}`);
  if (!res.ok) return false;
  return ((await res.json() as { value: unknown[] }).value ?? []).length > 0;
}

/**
 * Ensure the current user has the target role. Safe + idempotent + inert until
 * the role exists. Reads a per-session guard so it runs at most once per load.
 */
let attempted = false;
export async function ensureCurrentUserProvisioned(pca: IPublicClientApplication): Promise<ProvisioningResult> {
  if (attempted) return { status: 'already-had-role', detail: 'Already attempted this session.' };
  attempted = true;
  try {
    const role = await findTargetRole(pca);
    if (!role) {
      return { status: 'role-not-found', detail: `No security role matching "${TARGET_ROLE_MATCH}" exists yet — provisioning is inert until it is created in the Maker Portal.` };
    }
    const uid = await currentSystemUserId(pca);
    if (!uid) return { status: 'no-systemuser', detail: 'Could not resolve current systemuser (WhoAmI).' };
    if (await userHasRole(pca, uid, role.roleid)) {
      return { status: 'already-had-role', detail: `User already has "${role.name}".` };
    }
    // Associate the role (ADD only — never removes/reassigns anything).
    const res = await dvFetch(pca, `systemusers(${uid})/systemuserroles_association/$ref`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ '@odata.id': `${getDataverseApiBase()}/roles(${role.roleid})` }),
    });
    if (res.ok) return { status: 'assigned', detail: `Assigned "${role.name}" to the current user.` };
    if (res.status === 403) return { status: 'insufficient-privilege', detail: `Role "${role.name}" exists but this session cannot assign it — use the Power Automate provisioning flow (see docs/provisioning-flow.md).` };
    return { status: 'error', detail: `Assignment failed (${res.status}): ${(await res.text()).slice(0, 200)}` };
  } catch (e) {
    return { status: 'error', detail: e instanceof Error ? e.message : 'Provisioning error.' };
  }
}

/** Read-only readiness probe (no writes) — for an admin/setup surface to show
 *  whether the provisioning target is live yet. */
export async function getProvisioningReadiness(pca: IPublicClientApplication): Promise<{ roleExists: boolean; roleName: string | null }> {
  try {
    const role = await findTargetRole(pca);
    return { roleExists: !!role, roleName: role?.name ?? null };
  } catch {
    return { roleExists: false, roleName: null };
  }
}
